import { Injectable } from '@angular/core'
import { SegmentService } from '../../shared/analytics/services/segment.service'
import { SegmentPageProperties, SegmentTagV2 } from '../../shared/analytics/models/segment-tag.model'
import { VerticalService } from '../../singleton-services/vertical.service'
import {
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  PageMetaService
} from '../../libs/page-meta/page-meta.service'
import { take } from 'rxjs/operators'
import { buildContentTagString } from '../../shared/analytics/services/segment.utility'
import type { CoreAPITag } from '../../shared/analytics/services/segment.utility'

/**
 * The standard Page tag for the single article page view.
 *
 * @remarks
 * This is expected to be used with pageV2, as indicated by the use of the
 * SegmentTagV2 interface and `autoPageCall: false`.
 *
 * @see SegmentService#pageV2
 * @see SegmentTagV2
 */
export const ARTICLE_TAG: SegmentTagV2 = {
  page_category: 'News',
  subpage_category: 'Article',
  category: 'Articles',
  node_type: 'Article',
  autoPageCall: false
}

interface ArticleDetailAnalyticsProps {
  /** The Node ID of the Article */
  id: number | string
  is_premium: boolean
  primary_association_id: number | undefined
  /** If tags are present on the page */
  content_tags: string | undefined
}

/**
 * @see {@link https://flocasts.atlassian.net/wiki/spaces/ENG/pages/2155381155/Articles}
 */
function articleDetailProps(article: ArticleDetailAnalyticsProps): SegmentPageProperties {
  return {
    _tag: 'entity',
    node_id: article.id,
    is_premium: article.is_premium,
    content_tags: article.content_tags,
    primary_association_id: article.primary_association_id
  }
}

interface ArticleMetaDeps {
  id: number
  title: string
  seo_description: string
  shareable_link: string
  external_url: string | null
  asset_url: string
  publish_start_date: string | null
  modified_at: string | null
  author: null | {
    first_name: string | null
    last_name: string | null
  }
  node: {
    categories: ReadonlyArray<{ name: string }>
    tags: ReadonlyArray<CoreAPITag>
  }
  premium: boolean
}

/**
 * Handler for all article-related analytics activities like page/track calls.
 *
 * @see SegmentService
 * @see SegmentTagV2
 */
@Injectable({
  providedIn: 'root'
})
export class ArticleAnalyticsService {
  // prettier-ignore
  constructor(
    private readonly segmentService: SegmentService,
    private readonly verticalService: VerticalService,
    private readonly pageMeta: PageMetaService
  ) {}

  /**
   * Send a page call for a single-article page.
   *
   * @see {@link https://flocasts.atlassian.net/wiki/spaces/ENG/pages/2155381155/Articles}
   */
  public pageArticleDetail(props: ArticleDetailAnalyticsProps): void {
    this.segmentService.pageV2(ARTICLE_TAG, articleDetailProps(props))
  }

  public setPageMeta(article: ArticleMetaDeps): void {
    this.verticalService.siteSettings$.pipe(take(1)).subscribe(settings => {
      const titleSuffix: string = !settings.site_name ? '' : ` - ${settings.site_name}`
      const tags = buildContentTagString(article.node?.tags)

      this.pageMeta.setPageMeta({
        id: article.id,
        title: article.title + titleSuffix,
        type: 'article',
        twitterCard: 'summary_large_image',
        description: article.seo_description,
        canonicalUrl: article.external_url || article.shareable_link,
        image: {
          url: article.asset_url,
          width: DEFAULT_OG_IMAGE_WIDTH,
          height: DEFAULT_OG_IMAGE_HEIGHT
        },
        datePublished: article.publish_start_date,
        modifiedAt: article.modified_at,
        authorFirstname: article.author?.first_name ?? null,
        authorLastname: article.author?.last_name ?? null,
        categories: article.node?.categories,
        premium: article.premium,
        sportName: settings.sport_name,
        siteName: settings.site_name,
        contentTags: tags ? tags : null
      })
    })
  }
}
